/**
 * @file Components/Sections/Services.jsx
 * @description Services overview section listing travel services offered
 * such as visa assistance, hotel booking, and custom trip planning.
 */

import React from 'react';
import PropTypes from 'prop-types';

const Services = ({ services = [], title = "Our Services" }) => {
  const defaultServices = [
    {
      icon: "🏨",
      title: "Hotel Booking",
      description: "Find and book the perfect accommodation for your stay"
    },
    {
      icon: "✈️",
      title: "Flight Booking",
      description: "Book domestic and international flights at best prices"
    },
    {
      icon: "🚗",
      title: "Car Rental",
      description: "Rent a car for comfortable and convenient travel"
    },
    {
      icon: "🗺️",
      title: "Tour Packages",
      description: "Customized tour packages for memorable experiences"
    },
    {
      icon: "📋",
      title: "Travel Insurance",
      description: "Comprehensive travel insurance for peace of mind"
    },
    {
      icon: "🎫",
      title: "Visa Assistance",
      description: "Complete visa processing and documentation support"
    }
  ];

  const servicesToShow = services.length > 0 ? services : defaultServices;

  return (
    <div className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Comprehensive travel services for all your needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesToShow.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="text-3xl mr-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {service.title}
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                {service.description}
              </p>

              {service.features && service.features.length > 0 && (
                <ul className="text-sm text-gray-600 mb-4">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center mb-1">
                      <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              )}

              <button className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200">
                Learn More →
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

Services.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  title: PropTypes.string
};

export default Services;
